import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { apiFetch } from "../lib/api.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { useToast } from "../components/Toast.js";

// ADR-0085 Phase 2 (revisited 2026-05-26): API providers (e.g. Plex) do NOT
// share via this surface — every consumer wants different `inputs`, so the
// reuse model is "one integration, many per-node configurations." This page
// shows connector-routed SQL providers only. Plex API call configuration
// lives on each plex_api operation node, not here.

type Connector = {
  id: string;
  name: string;
};

type DataProvider = {
  id: string;
  name: string;
  description: string | null;
  provider_kind?: "connector_sql" | "plex_api";
  connector_id: string | null;
  connector_name: string | null;
  updated_at: string;
  used_by_count: number;
  source_sql_locked: boolean;
  /** ADR-0146: output shape frozen — flagged if a run changes the columns. */
  schema_locked?: boolean;
};

export function DataProviders() {
  const nav = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [rows, setRows] = useState<DataProvider[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [connectorId, setConnectorId] = useState("");
  const [sql, setSql] = useState("SELECT 1");

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [connectorsData, providersData] = await Promise.all([
        apiFetch<{ connectors: Connector[] }>("/api/tenant/connectors"),
        // Server still returns all kinds (legacy data); filter to connector_sql
        // for display since this surface is connector-SQL-only.
        apiFetch<{ data_providers: DataProvider[] }>("/api/tenant/data-providers")
      ]);
      setConnectors(connectorsData.connectors);
      setRows(providersData.data_providers.filter((p) => (p.provider_kind ?? "connector_sql") === "connector_sql"));
      if (!connectorId && connectorsData.connectors[0]?.id) {
        setConnectorId(connectorsData.connectors[0].id);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function createProvider() {
    if (!name.trim() || !connectorId || !sql.trim()) return;
    setSaving(true);
    try {
      await apiFetch("/api/tenant/data-providers", {
        method: "POST",
        body: {
          name: name.trim(),
          description: description.trim() || null,
          provider_kind: "connector_sql",
          connector_id: connectorId,
          source_spec: { specVersion: "source.v1", kind: "sql", sql: sql.trim() }
        }
      });
      setCreateOpen(false);
      setName("");
      setDescription("");
      setSql("SELECT 1");
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  }

  async function duplicateProvider(row: DataProvider) {
    try {
      await apiFetch(`/api/tenant/data-providers/${encodeURIComponent(row.id)}/duplicate`, { method: "POST", body: {} });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  async function deleteProvider(row: DataProvider) {
    const ok = await confirm.confirm({
      title: "Delete data provider?",
      detail: `This deletes "${row.name}".`,
      confirmLabel: "Delete"
    });
    if (!ok) return;
    try {
      await apiFetch(`/api/tenant/data-providers/${encodeURIComponent(row.id)}`, { method: "DELETE" });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  return (
    <PageLayout
      title="Data Providers"
      actions={
        <button className="adminNewLink" type="button" onClick={() => setCreateOpen(true)}>
          + New data provider
        </button>
      }
    >
      <div className="card adminList">
        {loading ? <LoadingState /> : null}
        {!loading && error ? <ErrorState detail={error} /> : null}
        {!loading && !error && rows.length === 0 ? <div className="adminListEmpty">No data providers</div> : null}
        {!loading && !error && rows.length > 0 ? (
          <div className="tableWrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Connector</th>
                  <th>Used by</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <button className="adminListName" type="button" onClick={() => nav(`/app/build/providers/${row.id}`)}>
                        {row.name}
                      </button>
                      {row.source_sql_locked ? (
                        <span className="pill" style={{ marginLeft: 8, fontSize: 11 }}>
                          SQL locked
                        </span>
                      ) : null}
                      {row.schema_locked ? (
                        <span className="pill" style={{ marginLeft: 8, fontSize: 11 }}>
                          Schema locked
                        </span>
                      ) : null}
                    </td>
                    <td>{row.connector_name ?? "Unassigned"}</td>
                    <td>{row.used_by_count}</td>
                    <td>{new Date(row.updated_at).toLocaleString()}</td>
                    <td>
                      <div className="adminListActions">
                        <button className="adminListAction" type="button" onClick={() => nav(`/app/build/providers/${row.id}`)}>
                          Edit
                        </button>
                        <button className="adminListAction" type="button" onClick={() => void duplicateProvider(row)}>
                          Duplicate
                        </button>
                        <button className="adminListAction isDanger" type="button" onClick={() => void deleteProvider(row)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {createOpen ? (
        <div className="modalOverlay" role="presentation" onClick={() => setCreateOpen(false)}>
          <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="reportModalTitle">New data provider</div>
            <div className="field">
              <label className="label" htmlFor="dp-name">
                Name
              </label>
              <input id="dp-name" className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </div>
            <div className="field">
              <label className="label" htmlFor="dp-description">
                Description
              </label>
              <textarea id="dp-description" className="input reportTextarea" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="field">
              <label className="label" htmlFor="dp-connector">
                Connector
              </label>
              <select id="dp-connector" className="input" value={connectorId} onChange={(e) => setConnectorId(e.target.value)}>
                <option value="" disabled>
                  Select connector
                </option>
                {connectors.map((connector) => (
                  <option key={connector.id} value={connector.id}>
                    {connector.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label className="label" htmlFor="dp-sql">
                SQL
              </label>
              <textarea id="dp-sql" className="input queryEditor" value={sql} onChange={(e) => setSql(e.target.value)} spellCheck={false} />
            </div>
            <div className="actions">
              <button className="btn" type="button" onClick={() => setCreateOpen(false)} disabled={saving}>
                Cancel
              </button>
              <button
                className="btn btnPrimary"
                type="button"
                onClick={() => void createProvider()}
                disabled={saving || !name.trim() || !connectorId || !sql.trim()}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      ) : null}

    </PageLayout>
  );
}
